import React, { useCallback, useMemo } from 'react'
import { IListSettings, IListSettingsColumn, IListSettingsReponse } from '@cbgms/api/modules/settings'
import { KnockOperator, KnockOrderByOptions, KnockTypes, defaultKnockOperators } from '@cbgms/components/grid/Knock/KnockTypes'
import { IColumn, PinnedStatus, SpecialIndex, onFilterChange, onFilterChangeOption } from '../Types'
import { useDebouncedCallback } from 'use-debounce'
import { isSpecialIndex } from '../utils'
import { IColumnSearchHistoryItem, useListSearchHistory } from './use-list-search-history'
import { ISetPaginationProperty, usePaginationManager } from './use-pagination-manager'
import { IPaginatedRequest, Pagination } from '@cbgms/api'

export interface ISettingsStoreParams extends IListSettings {
  unmodifiedColumns: IListSettingsColumn[]
}

export interface IUseListSettings {
  /**
   * A list of additional columns you want to display on top of the ones retrieved from the API
   */
  columns: IColumn[]

  /**
   * Optional Search query that will be appended to each search
   */
  predefinedSearch?: string

  scope: string

  /**
   * Called when the configuration of the grid has to be loaded
   */
  settingsLoader: () => Promise<IListSettingsReponse>

  /**
   * Called when the settings are updated and need to be saved
   */
  settingsStore: (settings: ISettingsStoreParams) => Promise<IListSettingsReponse>

  /**
   * Called when the settings data gets reset or cleared
   */
  settingsRemover: () => Promise<void>

  /**
   * UseApiSearchSettings: when this is true, it will use the api values for sorting and filtering
   * otherwise when settings are loaded the search/sort is reset.
   */
  UseApiSearchSettings?: boolean

  getUserID: () => string
}

export interface IUseListSettingsResult {
  headers: IColumn[]
  onColumnMove: (columns: IColumn[]) => Promise<void>
  onColumnResize: (columns: IColumn[]) => Promise<void>
  onFilterChange: onFilterChange
  onResetFilters: () => Promise<void>
  onSettingAdd: (column: string, type: KnockTypes) => Promise<void>
  onSettingRemove: (nameOfColumnToBeRemoved: string) => Promise<void>
  onSettingsReset: (hard?: boolean | undefined) => Promise<void>
  predefinedSearch?: string
  isSettingsLoading: boolean
  searchHistory: IColumnSearchHistoryItem[]
  setActiveFilters: (columns: IColumn[]) => void
  handlePageChange: (loadData: (additionalParams?: IPaginatedRequest | undefined) => Promise<void>, page: number) => Promise<void>
  handleLimitChange: (loadData: (additionalParams?: IPaginatedRequest | undefined) => Promise<void>, limit: number) => Promise<void>
  setPaginationProperty: (args: ISetPaginationProperty) => void
  getPaginationProperty: (propertyName: Pagination) => number | undefined
}

const combineSearches = (s1?: string, s2?: string): string => {
  // remove empty and recombine
  return [s1, s2].filter(s => !!s).join(',')
}

const findLastIndexByStatus = (columns: IListSettingsColumn[], pinnedStatus: PinnedStatus) => {
  return columns
    .filter(column => column.Pinned === pinnedStatus.toString())
    .reduce((previousColumnIndex, currentColumn) => {
      return currentColumn.Index > previousColumnIndex ? currentColumn.Index : previousColumnIndex
    }, -1)
}

function mapColumnSettingToEmptyColumnFilters(column: IListSettingsColumn): IListSettingsColumn {
  return {
    ...column,
    FilterOperator: '',
    FilterValue: ''
  }
}

function mapColumnSettingToEmptyColumnFiltersAndSorting(column: IListSettingsColumn): IListSettingsColumn {
  return {
    ...column,
    FilterOperator: '',
    FilterValue: '',
    SortDirection: '',
    SortIndex: 0
  }
}

function mapColumnsToHeaders(settingsColumns: IListSettingsColumn[], additionalColumns: IColumn[]) {
  const mappedSettingsColumns = settingsColumns.map(
    column =>
      ({
        key: column.Name,
        index: column.Index,
        pinned: column.Pinned ? column.Pinned : PinnedStatus.Center,
        width: column.Width,
        filter: column.FilterValue ?? '',
        filterOperator: column.FilterOperator ?? '',
        sortOrder: column.SortDirection ?? '',
        sortIndex: column.SortIndex ?? 0,
        visible: column.Visible ?? true
      } as IColumn)
  )

  const nonSpecialColumns = additionalColumns.filter(column => !isSpecialIndex(column.index))

  const result = [...mappedSettingsColumns, ...nonSpecialColumns]

  const potentialFirstColumn = additionalColumns.find(column => column.index === SpecialIndex.First)
  if (potentialFirstColumn) result.unshift(potentialFirstColumn)

  const potentialLastColumn = additionalColumns.find(column => column.index === SpecialIndex.Last)
  if (potentialLastColumn) result.push(potentialLastColumn)

  return result
}

function isStaticColumn(column: IColumn): boolean {
  return column.static === true
}

function isVisibleColumn(column: IColumn): boolean {
  return column.visible === true
}

function isActionColumn(column: IColumn): boolean {
  return column.index === SpecialIndex.Last
}

interface SettingsAssignableColumn {
  key: string
  name: string
  width: number
  index: number
  pinned: PinnedStatus
  filter: string
  filterOperator: KnockOperator
  sortOrder: KnockOrderByOptions
  sortIndex: number
  visible: boolean
}

function isSettingsColumnAssignable(column: IColumn): column is SettingsAssignableColumn {
  const predicates: (() => boolean)[] = [
    () => typeof column.key === 'string',
    () => typeof column.width === 'number',
    () => typeof column.index === 'number',
    () => Object.values(PinnedStatus).includes(column.pinned),
    () => typeof column.filter === 'string',
    () => {
      if (column.filterOperator !== undefined) {
        return Object.values(KnockOperator).includes(column.filterOperator)
      }

      return false
    },
    () => {
      if (column.sortOrder !== undefined) {
        return Object.values(KnockOrderByOptions).includes(column.sortOrder)
      }

      return false
    },
    () => typeof column.sortIndex === 'number'
  ]

  return predicates.every(predicate => predicate())
}

export const useListSettings = (props: IUseListSettings): IUseListSettingsResult => {
  const [isSettingsLoading, setIsSettingsLoading] = React.useState(true)
  const [settings, setSettings] = React.useState<IListSettings>({ Columns: [], PredefinedSearch: '' })

  const loadSettings = useCallback(async () => {
    try {
      const settingsData = await props.settingsLoader()
      if (settingsData && settingsData.Data) {
        const {
          Data: { Columns, PredefinedSearch }
        } = settingsData
        const settings = {
          Columns: Columns?.map(!props.UseApiSearchSettings ? mapColumnSettingToEmptyColumnFilters : column => column) || [],
          PredefinedSearch: props.UseApiSearchSettings ? PredefinedSearch : ''
        }
        setSettings(settings)
      } else {
        console.error('Invalid settings data')
      }
    } catch (e: any) {
      if (e.code === 404) {
        setSettings({ Columns: [], PredefinedSearch: '' })
      } else {
        console.error(e)
        throw e
      }
    } finally {
      setIsSettingsLoading(false)
    }
  }, [props.settingsLoader, setSettings, setIsSettingsLoading])

  React.useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const { handlePageChange, handleLimitChange, setPaginationProperty, getPaginationProperty } = usePaginationManager(props)
  const { onNewFilterSettings: onNewLocalStorageFilterSettings, history } = useListSearchHistory(props)

  const saveSettings = useCallback(
    (columns: IColumn[]): IListSettings => {
      const initialValue: IListSettingsColumn[] = []

      const columnSettings = columns.reduce((columns, currentColumn) => {
        if (isStaticColumn(currentColumn) || !isVisibleColumn(currentColumn) || isActionColumn(currentColumn)) {
          return columns
        }

        if (isSettingsColumnAssignable(currentColumn)) {
          columns.push({
            Name: currentColumn.key,
            Index: currentColumn.index,
            Pinned: currentColumn.pinned,
            Width: currentColumn.width,
            SortIndex: currentColumn.sortIndex,
            SortDirection: currentColumn.sortOrder,
            FilterOperator: currentColumn.filterOperator,
            FilterValue: currentColumn.filter,
            Visible: currentColumn.visible
          })
        }

        return columns
      }, initialValue)

      const result = {
        PredefinedSearch: settings.PredefinedSearch,
        Columns: columnSettings
      }

      setSettings(result)

      return result
    },
    [setSettings, settings.PredefinedSearch]
  )

  const onSettingAdd = useCallback(
    async (column: string, type: KnockTypes) => {
      const nextIndex = findLastIndexByStatus(settings.Columns, PinnedStatus.Center) + 1
      const columns = [
        ...settings.Columns.map(column => ({
          ...column,
          Index: column.Index >= nextIndex ? column.Index + 1 : column.Index
        })),
        {
          Name: column,
          Index: nextIndex,
          Pinned: PinnedStatus.Center,
          Width: 300,
          SortIndex: 0,
          SortDirection: '',
          FilterOperator: defaultKnockOperators[type] || '=',
          FilterValue: '',
          Visible: true
        }
      ]
      await props.settingsStore({
        PredefinedSearch: settings.PredefinedSearch,
        Columns: columns,
        unmodifiedColumns: columns
      })

      await loadSettings()
    },
    [settings.Columns, props.settingsStore, loadSettings, settings.PredefinedSearch]
  )

  const onSettingRemove = useCallback(
    async (nameOfColumnToBeRemoved: string) => {
      const columnToBeRemoved = settings.Columns.find(column => column.Name === nameOfColumnToBeRemoved)

      if (!columnToBeRemoved) return

      const columns = settings.Columns.map(column => ({
        ...column,
        Index: column.Index > columnToBeRemoved.Index ? column.Index - 1 : column.Index
      })).filter(column => column.Name !== nameOfColumnToBeRemoved)
      await props.settingsStore({
        PredefinedSearch: settings.PredefinedSearch,
        Columns: columns,
        unmodifiedColumns: columns
      })

      await loadSettings()
    },
    [settings.Columns, props.settingsStore, loadSettings, settings.PredefinedSearch]
  )

  const onSettingsReset = useCallback<(hard?: boolean) => Promise<void>>(
    async (hard = false) => {
      if (hard) {
        setIsSettingsLoading(true)
      }

      await props.settingsRemover()

      await loadSettings()

      if (hard) {
        setIsSettingsLoading(false)
      }
    },
    [setIsSettingsLoading, props.settingsRemover, loadSettings]
  )

  const onResetFilters = useCallback(async () => {
    const data = await props.settingsStore({
      PredefinedSearch: settings.PredefinedSearch,
      Columns: settings.Columns.map(mapColumnSettingToEmptyColumnFiltersAndSorting),
      unmodifiedColumns: settings.Columns
    })

    setSettings(data.Data)
  }, [settings.Columns, props.settingsStore, setSettings, settings.PredefinedSearch])

  const onColumnMove = useCallback(
    async (columns: IColumn[]) => {
      const result = saveSettings(columns)
      await props.settingsStore({ ...result, unmodifiedColumns: result.Columns })
    },
    [saveSettings, props.settingsStore]
  )

  const onColumnResize = useCallback(
    async (columns: IColumn[]) => {
      const result = saveSettings(columns)
      await props.settingsStore({ ...result, unmodifiedColumns: result.Columns })
    },
    [saveSettings, props.settingsStore]
  )

  const debouncedSettingsStore = useDebouncedCallback(props.settingsStore, 500)

  // onFilterChange is used when typing in the boxes, it has a side effect of storing the search in local storage
  const onFilterChange = useCallback(
    async (columns: IColumn[], options?: onFilterChangeOption) => {
      const { Columns, PredefinedSearch } = saveSettings(columns)
      if (!options?.skipApiStorage) {
        debouncedSettingsStore({
          unmodifiedColumns: Columns,
          Columns: Columns.map(mapColumnSettingToEmptyColumnFilters),
          PredefinedSearch
        })
      }
      if (!options?.skipLocalStorage) {
        onNewLocalStorageFilterSettings(Columns)
      }
    },
    [saveSettings, props.settingsStore, onNewLocalStorageFilterSettings]
  )

  // setActiveFilters will apply the provided filters as
  const setActiveFilters = useCallback(
    async (columns: IColumn[]) => onFilterChange(columns, { skipApiStorage: true, skipLocalStorage: false }),
    [onFilterChange]
  )

  const predefinedSearch = useMemo(
    () => combineSearches(props.predefinedSearch, settings.PredefinedSearch),
    [props.predefinedSearch, settings.PredefinedSearch]
  )
  const headers = useMemo(() => mapColumnsToHeaders(settings.Columns, props.columns), [settings.Columns, props.columns])

  return useMemo(
    () => ({
      headers,
      onColumnMove,
      onColumnResize,
      onFilterChange,
      onResetFilters,
      onSettingAdd,
      onSettingRemove,
      onSettingsReset,
      predefinedSearch,
      isSettingsLoading,
      searchHistory: history,
      setActiveFilters,
      handlePageChange,
      handleLimitChange,
      setPaginationProperty,
      getPaginationProperty
    }),
    [
      headers,
      onColumnMove,
      onColumnResize,
      onFilterChange,
      onResetFilters,
      onSettingAdd,
      onSettingRemove,
      onSettingsReset,
      predefinedSearch,
      isSettingsLoading,
      history,
      setActiveFilters,
      handlePageChange,
      handleLimitChange,
      setPaginationProperty,
      getPaginationProperty
    ]
  )
}
